import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '@environments/environment';
import { User } from '@app/_models';
import { ApplicationInfoDto, GetCurrentLoginInformationsOutput, SessionService, TokenAuthService, UserLoginInfoDto } from 'openapi';
import { AppConsts } from '../../shared/AppConsts';
import { CookieService } from 'ngx-cookie-service';
import { AlertController, NavController, Platform } from '@ionic/angular';
import { Network } from '@ionic-native/network/ngx';
import { Globals } from '@app/globals';
import { SSOAuthService } from './sso.service';
import { StringUtils } from '@shared/utils/StringUtils';

@Injectable({ providedIn: 'root' })
export class AccountService {

    private _user: UserLoginInfoDto;
    private _appinfo : ApplicationInfoDto;
    
    private userSubject: BehaviorSubject<User>;
    public user: Observable<User>;

    constructor(
        private _router: Router,
        private _sessionService: SessionService,
        private http: HttpClient,
        private _cookieService: CookieService,
        private alertCtrl: AlertController,
        public navCtrl: NavController,
        private platform: Platform,
        private network: Network,
        public globals: Globals,
        private ssoAuthService: SSOAuthService,
        private tokenService: TokenAuthService,
    ) {
        this.userSubject = new BehaviorSubject<User>(JSON.parse(localStorage.getItem('user')));
        this.user = this.userSubject.asObservable();
    }

    get userLoggedIn(): UserLoginInfoDto {
        return this._user;
    }

    public get userValue(): User {
        return this.userSubject.value;
    }
    public get appInfo():ApplicationInfoDto{
        return this._appinfo;
    }

    login(username, password) {
        return this.http.post<User>(`${AppConsts.remoteServiceBaseUrl}/users/authenticate`, { username, password })
            .pipe(map(user => {
                // store user details and jwt token in local storage to keep user logged in between page refreshes
                localStorage.setItem('user', JSON.stringify(user));
                this.userSubject.next(user);
                return user;
            }));
    }

    init(): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this._sessionService.apiSessionCurrentLoginInformationsGet().toPromise().then((result: GetCurrentLoginInformationsOutput) => {
                this._user = result.user;
                this._appinfo = result.application;
                resolve(true);
            }, (err) => {
                /**
                 * for mobile it is creating a loop on splash and select country, 
                 * to avoid existing behaviour of web, added platform condition here
                 */
                if (!StringUtils.isMobilePlatform(this.platform))
                    this.logout();
                else
                    resolve(false)
            });
        });
    }


    setCurrentLoggedInUserInfo(accessToken: string): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            this._sessionService.apiSessionCurrentLoginInformationsGet().toPromise().then((result: GetCurrentLoginInformationsOutput) => {
                this._user = result.user;
                this._appinfo = result.application;
                resolve(true);
            }, (err) => {
               this.logout();
            });
        });
    }
    

    logout(reload?: boolean): void {
        var email = localStorage.getItem('_e');
        var password = localStorage.getItem('_p');
        var countryCode = localStorage.getItem('_cc');
        var phoneNumber = localStorage.getItem('_pn');
        var rememberMe = localStorage.getItem('MemberRememberMe');

        window.localStorage.removeItem('Abp.TenantId');
        window.localStorage.removeItem('companyId');
        //window.localStorage.removeItem('MemberRememberMe');
        
        
        abp.utils.deleteCookie(AppConsts.authorization.encryptedAuthTokenName, '/');
        abp.utils.deleteCookie(AppConsts.authorization.AuthToken, '/');
        if (rememberMe == 'true') {
            localStorage.setItem('MemberRememberMe', rememberMe);
            localStorage.setItem('_e', email);
            localStorage.setItem('_p', password);
            localStorage.setItem('_cc', countryCode);
            localStorage.setItem('_pn', phoneNumber);

        }
        if(!StringUtils.isMobilePlatform(this.platform) || this.platform.is('mobileweb')){
           
            if (reload) {
            this._router.navigate(['account/login']);
           
            }
           
        } else{
            // We need to call logout API as to clear cookies from native side (sslSupport plugin)
            abp.ui.setBusy();
            this.tokenService.apiAccountLogoutPost(true).subscribe(res => {
                abp.ui.clearBusy();
                abp.utils.deleteCookie(AppConsts.authorization.encryptedAuthTokenName, '/');
                abp.utils.deleteCookie(AppConsts.authorization.AuthToken, '/');
                abp.utils.deleteCookie(AppConsts.SSO.token, '/');
                abp.utils.deleteCookie(AppConsts.SSO.refreshToken, '/');
                this.ssoAuthService.logout();
              });
            /* if (reload) {
                this.navCtrl.navigateRoot('/account/login');
            } */  
        }
        abp.ui.clearBusy();
      }

    register(user: User) {
        return this.http.post(`${AppConsts.remoteServiceBaseUrl}/users/register`, user);
    }

    getAll() {
        return this.http.get<User[]>(`${AppConsts.remoteServiceBaseUrl}/users`);
    }

    getById(id: string) {
        return this.http.get<User>(`${AppConsts.remoteServiceBaseUrl}/users/${id}`);
    }

    update(id, params) {
        return this.http.put(`${AppConsts.remoteServiceBaseUrl}/users/${id}`, params)
            .pipe(map(x => {
                // update stored user if the logged in user updated their own record
                if (id == this.userValue.id) {
                    // update local storage
                    const user = { ...this.userValue, ...params };
                    localStorage.setItem('user', JSON.stringify(user));

                    // publish updated user to subscribers
                    this.userSubject.next(user);
                }
                return x;
            }));
    }

    delete(id: string) {
        return this.http.delete(`${AppConsts.remoteServiceBaseUrl}/users/${id}`)
            .pipe(map(x => {
                // auto logout if the logged in user deleted their own record
                if (id == this.userValue.id.toString()) {
                    this.logout();
                }
                return x;
            }));
    }
   
}